import { useState, useMemo, useEffect, useRef, RefObject } from "react"
import { cloneDeep } from "lodash"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "@/hooks/useSearchParams"

import { SectionSessionDetails, SessionDetailsData } from "@/app/report/common/constructor"
import SectionContainer from "@/app/report/components/SectionContainer"
import MultipleChart from "@/app/report/components/chart/MultipleChart"
import { ExportChartRefType, MenuActionType } from "@/app/report/common/type"
import SectionMenu from "@/app/report/components/SectionMenu"
import IntervalChart from "@/app/report/components/chart/IntervalChart"
import {
  ACTIVITY_SESSION_TYPE,
  DEFAULT_EXPORT_OPTIONS,
  REPORT_TYPE,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import ReportSectionSessionSelector from "@/app/report/components/section/ReportSectionSessionSelector"
import Divider from "@/components/divider/Divider"
import Tab from "@/components/tab/Tab"
import { Interval } from "@/types/report"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { usePositionRank } from "@/hooks/usePositionRank"
import { buildReportSortKey, createReportSortStore } from "@/stores/reportSort"
import { sortChartRows } from "@/app/report/common/export/sortRows"

import "./sessionDetailsSection.scss"
import ExportChartDialog from "../dialog/ExportChartDialog"

export default function SessionDetails({
  reportSection,
  intervalValue,
  setIntervalValue,
  selectedSession,
  setSelectedSession,
  loading,
  intervalLoading,
  sectionIndex = 0,
  reportType = REPORT_TYPE.DAILY,
}: {
  reportSection: SectionSessionDetails
  intervalValue: Interval
  setIntervalValue: (value: Interval) => void
  selectedSession: number[]
  setSelectedSession: (value: number[]) => void
  loading: boolean
  intervalLoading: boolean
  sectionIndex?: number
  reportType?: REPORT_TYPE
}) {
  const { t } = useTranslation()
  const searchParams = useSearchParams()
  const translateKey = useTranslateMetricKey()
  const positionRank = usePositionRank()

  const dailyReportType = searchParams.get("dailyReportType")

  // 세션 상세 데이터가 아직 준비되지 않은 경우 (SESSION 모드 URL 직접 진입 등)
  const hasSessionData =
    reportSection?.sessionDetailsData?.length > 0 && !!reportSection.sessionDetailsData[0]

  const storeKey = buildReportSortKey(reportType, sectionIndex, 0)
  // 화면 정렬은 리포트 스코프 store 에 보존(템플릿 미저장). 없으면 템플릿(sortSession) seed.
  const storedSort = createReportSortStore((state) => state.sorts[storeKey])
  const setSort = createReportSortStore((state) => state.setSort)

  const sortValue: string[] = storedSort
    ? [storedSort.key, storedSort.order]
    : hasSessionData && reportSection.sessionDetailsData[0]?.sortSession
      ? [
          reportSection.sessionDetailsData[0].sortSession.key,
          reportSection.sessionDetailsData[0].sortSession.order.toLowerCase(),
        ]
      : []

  const sessionDetailData: SessionDetailsData | undefined = useMemo(() => {
    if (!hasSessionData) {
      return undefined
    }

    const initialData = reportSection.sessionDetailsData[0]
    if (!sortValue.length) {
      return initialData
    }

    const sortedData = cloneDeep(initialData)
    sortedData.dataSession = sortChartRows(
      sortedData.dataSession,
      sortedData.axisY,
      { key: sortValue[0], order: sortValue[1] as "asc" | "desc" },
      positionRank,
    )

    return sortedData
  }, [sortValue, reportSection, positionRank, hasSessionData])

  const [sessionSelectorOpen, setSessionSelectorOpen] = useState(false) //  NOTE Interval 영역
  const [exportFileType, setExportFileType] = useState<string>("")
  const [currentDecimalPoints, setCurrentDecimalPoints] = useState<number>(1)
  const [currentAnnotationOptions, setCurrentAnnotationOptions] = useState<VALUE_POSITION_TYPE>(
    VALUE_POSITION_TYPE.HIDE_VALUES,
  )
  const [selectedChartRef, setSelectedChartRef] =
    useState<RefObject<ExportChartRefType | null> | null>(null)

  const chartRef = useRef<ExportChartRefType>(null)
  const intervalRef = useRef<ExportChartRefType>(null)

  const idfSession = useMemo(
    () => searchParams.get("idfSession")?.split(",").map(Number),
    [searchParams],
  )

  // Initialize selectedSession from URL params on mount and when sessionList updates
  useEffect(() => {
    if (idfSession && idfSession.length && sessionDetailData?.sessionList?.length) {
      const validSessions = sessionDetailData.sessionList
        .filter((session) => idfSession.includes(session.idfSession))
        .map((session) => session.idfSession)

      if (validSessions.length && selectedSession.length === 0) {
        setSelectedSession(validSessions)
      }
    }
  }, [idfSession, sessionDetailData?.sessionList])

  const IntervalTabList = [
    {
      value: "1m",
      label: `1 ${t("mins")}`,
    },
    {
      value: "5m",
      label: `5 ${t("mins")}`,
    },
    {
      value: "15m",
      label: `15 ${t("mins")}`,
    },
  ]

  const menuItems = useMemo(() => {
    if (!hasSessionData) return []
    /* chart by player: value, player name, position
    export pdf, png image는 공통 필수 */
    const menuOptions = [] as MenuActionType[]
    menuOptions.push({
      name: "value",
      value: "value",
      group: "sort",
      children: reportSection.sessionDetailsData[0].settings.yAxis.map((yAxis) => {
        const newMetricName = translateKey(yAxis.metric)

        return {
          name: newMetricName,
          value: yAxis.metric,
          color: yAxis.color,
          children: [
            {
              name: "maxMin",
              value: `${yAxis.metric}MaxMin`,
              func: () => {
                setSort(storeKey, { key: yAxis.metric, order: "desc" })
              },
            },
            {
              name: "minMax",
              value: `${yAxis.metric}MinMax`,
              func: () => {
                setSort(storeKey, { key: yAxis.metric, order: "asc" })
              },
            },
          ],
        }
      }),
    })
    menuOptions.push({
      name: "time",
      value: "time",
      group: "sort",
      children: [
        {
          name: t("ascending"),
          value: "asc",
          func: () => setSort(storeKey, { key: "time", order: "asc" }),
        },
        {
          name: t("descending"),
          value: "desc",
          func: () => setSort(storeKey, { key: "time", order: "desc" }),
        },
      ],
    })
    menuOptions.push({
      name: "sessionName",
      value: "sessionName",
      group: "sort",
      children: [
        {
          name: "A - Z",
          value: "sessionNameAZ",
          func: () => setSort(storeKey, { key: "sessionName", order: "asc" }),
        },
        {
          name: "Z - A",
          value: "sessionNameZA",
          func: () => setSort(storeKey, { key: "sessionName", order: "desc" }),
        },
      ],
    })
    menuOptions.push(
      ...DEFAULT_EXPORT_OPTIONS.map((option) => ({
        ...option,
        func: () => {
          setExportFileType(option.value)
          setSelectedChartRef(chartRef)
        },
      })),
    )

    return menuOptions
  }, [reportSection])

  const renderMultipleChart = () => {
    if (dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY) {
      return null
    }

    return (
      <>
        <MultipleChart
          chartData={sessionDetailData as SessionDetailsData}
          loading={loading}
          ref={chartRef}
        />
        <Divider />
      </>
    )
  }

  if (!sessionDetailData) {
    return (
      <SectionContainer title={t("sessionDetails")} sectionMenu={<></>}>
        <div />
      </SectionContainer>
    )
  }

  return (
    <>
      <SectionContainer
        title={t("sessionDetails")}
        sectionMenu={
          sessionDetailData.dataSession.length ? <SectionMenu menuItems={menuItems} /> : <></>
        }>
        {renderMultipleChart()}
        <div className="IntervalSectionCont">
          <div className="interval-session-selector-wrapper">
            <Tab
              type="filled"
              list={IntervalTabList}
              value={intervalValue}
              onChange={(_, newVal) => setIntervalValue(newVal)}
            />
            <ReportSectionSessionSelector
              open={sessionSelectorOpen}
              setOpen={setSessionSelectorOpen}
              selectedSessionList={selectedSession || []}
              setSelectedSessionList={setSelectedSession}
              sessionList={sessionDetailData.sessionList.filter((session) =>
                idfSession?.includes(session.idfSession),
              )}
            />
            <SectionMenu
              menuItems={[
                ...DEFAULT_EXPORT_OPTIONS.map((option) => ({
                  ...option,
                  func: () => {
                    setExportFileType(option.value)
                    setSelectedChartRef(intervalRef)
                  },
                })),
              ]}
            />
          </div>
        </div>
        <IntervalChart
          chartData={sessionDetailData as SessionDetailsData}
          loading={intervalLoading}
          ref={intervalRef}
        />
      </SectionContainer>

      <ExportChartDialog
        open={!!exportFileType}
        setOpen={() => setExportFileType("")}
        decimalPoints={currentDecimalPoints}
        onChangeDecimalPoints={setCurrentDecimalPoints}
        annotationOptions={currentAnnotationOptions}
        onChangeAnnotationOptions={setCurrentAnnotationOptions}
        onConfirm={() => {
          if (!selectedChartRef || !selectedChartRef.current) {
            return
          }
          selectedChartRef.current.exportFile({
            exportType: exportFileType as "png" | "pdf",
            magnification: 1.0,
            reportType: window.location.pathname.includes(REPORT_TYPE.DAILY)
              ? REPORT_TYPE.DAILY
              : REPORT_TYPE.TREND,
            decimalPoints: currentDecimalPoints,
            chartAnnotation: currentAnnotationOptions,
          })
        }}
      />
    </>
  )
}
